'use client';

import React, { useState } from 'react';
import BaseMechanicShell from './BaseMechanicShell';
import { ListChecks } from 'lucide-react';
import { useMountShuffledList } from './shuffle-utils';

interface MultipleChoiceMechanicProps {
  lessonSlug: string;
  mechanicId?: string;
  id?: string;
  prompt?: string;
  tier?: string;
  localHints?: { id: string; hint: string }[];
  localValidator?: { kind?: string; correctAnswer?: string; hints?: any[] };
  payload: {
    question: string;
    options: Array<{ id: string; content: string }>;
    _hasHints?: boolean;
  };
}

export default function MultipleChoiceMechanic({ lessonSlug, mechanicId, id, prompt, tier, localHints, localValidator, payload }: MultipleChoiceMechanicProps) {
  const actualMechanicId = mechanicId || id || '';
  const question = payload?.question || '';

  return (
    <BaseMechanicShell
      lessonSlug={lessonSlug}
      mechanicId={actualMechanicId}
      prompt={question || prompt || ''}
      icon={<ListChecks className="w-5 h-5" />}
      iconLabel="Multiple Choice — Pick the correct answer"
      payload={payload}
      tier={tier}
      localHints={localHints}
      localValidator={localValidator}
    >
      {(submissionState, setAnswer) => (
        <MultipleChoiceInner
          payload={payload}
          submissionState={submissionState}
          setAnswer={setAnswer}
        />
      )}
    </BaseMechanicShell>
  );
}

function MultipleChoiceInner({ payload, submissionState, setAnswer }: any) {
  const options = useMountShuffledList(payload?.options || []);
  const question = payload?.question || '';
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const handleSelect = (optionId: string) => {
    if (submissionState === 'submitting' || submissionState === 'success') return;
    setSelectedOption(optionId);
    setAnswer(optionId);
  };

  return (
    <div className="flex flex-col gap-3">
      {options.map((option: any, index: number) => {
        const isSelected = selectedOption === option.id;
        return (
          <button
            type="button"
            key={option.id || index}
            disabled={submissionState === 'submitting' || submissionState === 'success'}
            onClick={() => handleSelect(option.id)}
            className={`
              text-left p-4 rounded-xl border-2 transition-all min-h-[48px] flex items-center text-base
              focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background
              ${
                submissionState === 'success' && isSelected
                  ? 'border-primary bg-primary text-primary-foreground font-medium'
                  : submissionState === 'success'
                  ? 'border-border/50 bg-background/50 opacity-50 cursor-not-allowed'
                  : isSelected
                  ? 'border-primary bg-primary/10 text-foreground font-medium shadow-[inset_0_0_0_1px_rgba(var(--primary),0.5)]'
                  : 'border-border bg-background hover:border-primary/50 hover:bg-muted/50 cursor-pointer text-foreground/80'
              }
            `}
          >
            {option.content}
          </button>
        );
      })}
    </div>
  );
}
